// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for all sqip legal documents.
//
// This module is intentionally framework-free (plain data, no JSX, no MUI) so
// that it can be consumed by BOTH:
//   1. The React pages (client-side rendering / in-app navigation), and
//   2. The Node prerender script (prerender-legal.mjs) that emits static HTML
//      for the /legal/* routes — satisfying the "content visible without
//      JavaScript" compliance requirement.
//
// Block model used by `content` arrays:
//   { type: 'p',  text: '...' }            → paragraph
//   { type: 'ul', items: ['...', '...'] }  → bullet list
// ─────────────────────────────────────────────────────────────────────────────

export const COMPANY = {
  legalName: 'SQIP Technologies Inc.',
  brand: 'sqip',
  website: 'sqipqueue.com',
  product:
    'a virtual queue and appointment management platform for clinics, salons, and service businesses',
  supportEmail: 'support@sqipqueue.com',
  privacyContactName: 'Akshay Mogaveera',
  privacyContactEmail: 'support@sqipqueue.com',
  // Public legal pages show city + province only (the registered address is a
  // home address, deliberately not published in full).
  address: 'Toronto, Ontario, Canada',
  jurisdiction: 'Ontario, Canada',
  effectiveDate: 'August 2026',
  smsPrice: "$0.004",
  emailPrice: "0.0003"

};

// ── Privacy Policy ────────────────────────────────────────────────────────────

const PRIVACY = {
  slug: 'privacy',
  title: 'Privacy Policy',
  subtitle:
    'How SQIP Technologies Inc. collects, uses, protects, and shares personal information across the sqip platform.',
  sections: [
    {
      heading: '1. Introduction',
      content: [
        {
          type: 'p',
          text: `SQIP Technologies Inc. ("sqip", "we", "us", or "our") operates ${COMPANY.brand} (${COMPANY.website}), ${COMPANY.product}. This Privacy Policy explains how we collect, use, disclose, retain, and safeguard personal information when you use our platform, and the rights you have over your information.`,
        },
        {
          type: 'p',
          text: 'We are committed to handling personal information in accordance with Canada\'s Personal Information Protection and Electronic Documents Act (PIPEDA) and, where applicable to health information in Ontario, the Personal Health Information Protection Act, 2004 (PHIPA). By using the sqip platform, you agree to the practices described in this policy.',
        },
      ],
    },
    {
      heading: '2. Information We Collect',
      content: [
        {
          type: 'p',
          text: 'We collect only the information needed to provide and operate the queue and appointment service:',
        },
        {
          type: 'ul',
          items: [
            'Phone number — used to verify your identity and to send you queue and appointment notifications.',
            'Email addresses — used for notifications and OTP.',
            'Name — used to identify you within a queue or appointment so staff and you can recognize your place.',
            'Queue and appointment data — your position in a queue, appointment times, the business you are visiting, and related status information.',
            'Device and technical information — basic device, browser, and connection details (such as IP address) collected automatically to operate the platform securely and reliably.',
          ],
        },
      ],
    },
    {
      heading: '3. How We Use Your Information',
      content: [
        { type: 'p', text: 'We use the information we collect to:' },
        {
          type: 'ul',
          items: [
            'Manage virtual queues and appointments and show you your status.',
            'Verify your identity through one-time passcode (OTP) verification.',
            'Send you appointment and queue notifications by SMS text message.',
            'Operate, maintain, secure, and improve the platform, including preventing abuse and fraud.',
          ],
        },
      ],
    },
    {
      heading: '4. SMS Notifications and Consent',
      content: [
        {
          type: 'p',
          text: 'sqip uses SMS text messaging to deliver one-time passcodes (OTP) and transactional notifications about your queue position and appointments.',
        },
        {
          type: 'p',
          text: 'You consent to receive these SMS messages when you enter your phone number and request an OTP, or when you join a queue or book an appointment through the platform. We send only service-related (transactional) messages; we do not send marketing or promotional SMS. Message frequency varies based on your queue and appointment activity. Message and data rates may apply.',
        },
        {
          type: 'p',
          text: 'SMS opt-out: You can unsubscribe at any time by replying STOP to any message. After you reply STOP, we will stop sending SMS notifications to that number. Note that opting out of SMS may limit your ability to use queue and appointment features that depend on text notifications.',
        },
        {
          type: 'p',
          text: 'We will not share or sell your mobile information with third parties for promotional or marketing purposes.',
        },
      ],
    },
    {
      heading: '5. Third-Party Subprocessors',
      content: [
        {
          type: 'p',
          text: 'We rely on a small number of trusted service providers ("subprocessors") to operate the platform. These providers process information only on our behalf and only to deliver their service to us:',
        },
        {
          type: 'ul',
          items: [
            'Telnyx — delivery of SMS messages (OTP and notifications).',
            'Stripe — payment processing for business subscriptions.',
            'Railway — application hosting and infrastructure.',
          ],
        },
      ],
    },
    {
      heading: '6. We Do Not Sell Your Data',
      content: [
        {
          type: 'p',
          text: 'sqip does not sell, rent your personal information to third parties. We share information only with the subprocessors listed above to operate the service, or where required by law.',
        },
      ],
    },
    {
      heading: '7. Your Rights',
      content: [
        {
          type: 'p',
          text: 'You have the right to access the personal information we hold about you, to request correction of inaccurate information, and to request deletion of your information. We will respond to verified requests within 30 days.',
        },
        {
          type: 'p',
          text: `To exercise these rights, contact our privacy contact, ${COMPANY.privacyContactName}, at ${COMPANY.privacyContactEmail}.`,
        },
      ],
    },
    {
      heading: '8. Data Security and Breach Notification',
      content: [
        {
          type: 'p',
          text: 'We use technical and organizational safeguards designed to protect personal information against loss, theft, and unauthorized access, use, or disclosure.',
        },
        {
          type: 'p',
          text: 'In the event of a breach of security safeguards involving personal information that poses a real risk of significant harm, we will notify affected individuals and the appropriate authorities as required by PIPEDA (and PHIPA where applicable), within a reasonable time frame and will take steps to contain and remediate the breach.',
        },
      ],
    },
    {
      heading: '9. Health Information (PHIPA)',
      content: [
        {
          type: 'p',
          text: 'Where a business using sqip is a health information custodian under PHIPA (for example, a clinic), that business remains the custodian of patient personal health information. sqip acts as that business\'s agent and information processor, handling such information only to provide the queue and appointment service and only on the business\'s instructions.',
        },
      ],
    },
    {
      heading: '10. Governing Law',
      content: [
        {
          type: 'p',
          text: `This Privacy Policy is governed by the laws of ${COMPANY.jurisdiction}, and the applicable federal laws of Canada.`,
        },
      ],
    },
    {
      heading: '11. Changes to These Terms',
      content: [
        {
          type: 'p',
          text: 'We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the updated Terms.',
        },
        {
          type: 'p',
          text: 'We will update the "Last Updated" date at the top of this page when changes are made.',
        },
      ],
    },

    {
      heading: '12. Contact Us',
      content: [
        {
          type: 'p',
          text: `Privacy contact: ${COMPANY.privacyContactName} — ${COMPANY.privacyContactEmail}.`,
        },
        {
          type: 'p',
          text: `General support: ${COMPANY.supportEmail}. Location: ${COMPANY.address}.`,
        },
      ],
    },
  ],
};

// ── User Terms of Service ─────────────────────────────────────────────────────

const USER_TERMS = {
  slug: 'terms',
  title: 'User Terms of Service',
  subtitle:
    'The terms that govern your use of the sqip platform as an individual user joining queues or booking appointments.',
  sections: [
    {
      heading: '1. About sqip',
      content: [
        {
          type: 'p',
          text: `SQIP Technologies Inc. operates ${COMPANY.brand} (${COMPANY.website}), ${COMPANY.product}. The platform lets you join virtual queues and book appointments with participating businesses and receive status updates so you don't have to wait in line in person.`,
        },
        {
          type: 'p',
          text: 'These User Terms of Service ("Terms") form an agreement between you and sqip. By using the platform, you agree to these Terms.',
        },
      ],
    },
    {
      heading: '2. Eligibility',
      content: [
        {
          type: 'p',
          text: 'You must be 18 years of age or over, or the legal age to form a binding contract in your jurisdiction if that age is greater than 18 years of age, to create a sqip Account or use the Services on your own. Those under the age of 18 may not use the Services. If you are using the Services on behalf of a minor child, you represent and warrant that you are the parent or legal guardian of such child, and that all references in these Terms of Use to “you” shall refer to such child or such other individual on whose behalf you have authorization to enter into these Terms of Use and you in your capacity as the parent or legal guardian of such child or as the authorized party of such individual. ',
        },
      ],
    },
    {
      heading: '3. Account Creation and Phone Verification',
      content: [
        {
          type: 'p',
          text: 'To use the platform you provide your phone number, which we verify using a one-time passcode (OTP) sent by SMS. You are responsible for keeping access to your phone number secure and for the activity that occurs under your account.',
        },
      ],
    },
    {
      heading: '4. Acceptable Use',
      content: [
        { type: 'p', text: 'When using the platform, you agree not to:' },
        {
          type: 'ul',
          items: [
            'Abuse, harass, or interfere with other users, businesses, or staff.',
            'Send spam or submit false, fraudulent, or duplicate queue or appointment entries.',
            'Enter phone numbers you do not own or control, or attempt to send unsolicited messages to others through the platform.',
            'Attempt to disrupt, overload, reverse-engineer, or gain unauthorized access to the platform or its systems.',
          ],
        },
      ],
    },
    {
      heading: '5. System Integrity and Data Scraping',
      content: [
        {
          type: 'p',
          text: 'You must not use the software in any manner that could degrade, crash, overload, or structurally compromise our servers or communication channels. You are explicitly blocked from executing automated data mining, systematic extraction, text harvesting, or spider-crawling across any scheduling interfaces or queue dashboards maintained by Sqip.',
        },
      ],
    },
    {
  heading: '6. Bans For Malicious Actors',
  content: [
    {
      type: 'p',
      text: 'Sqip reserves the right to permanently block a mobile phone number or device identifier from accessing any virtual queue or appointment feature across our platform, without prior notice, explanation, or liability, where we reasonably believe that number or device has been used for abusive, fraudulent, or malicious activity. Any coordinated attempt to bypass rate limits or spoof text messages may result in escalation to our security processes and referral to law enforcement or legal counsel where appropriate.',
    },
  ],
},,
    {
      heading: '7. SMS Consent',
      content: [
        {
          type: 'p',
          text: 'By providing your phone number, you consent to receive SMS text messages from sqip, including one-time passcodes (OTP) and transactional notifications about your queue position and appointments. These are service messages, not marketing. Message frequency varies. Message and data rates may apply.',
        },
        {
          type: 'p',
          text: 'Opt-out: You can unsubscribe from SMS at any time by replying STOP to any message. Opting out may limit features that rely on text notifications.',
        },
      ],
    },
      {
      heading: '8. Unilateral Traffic Throttling',
      content: [
        {
          type: 'p',
          text: 'To protect system security and prevent cost inflation across upstream cellular text links, Sqip implements strict automated rate-limiting protocols. If a User is flagged as exhibiting malicious behavior, suspicious high-frequency patterns, or repetitive OTP triggers, Sqip will instantly and unilaterally throttle, restrict, or completely block that User\'s device IP and phone number from our systems.',
        },
      ],
          
      },
    {
      heading: '9. Limitation of Liability',
      content: [
        {
          type: 'p',
          text: 'To the maximum extent permitted by law, sqip and its officers, employees, and suppliers will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for lost time, missed appointments, or service interruptions, arising from or related to your use of (or inability to use) the platform. sqip is a technology provider and is not responsible for the services, decisions, or conduct of the businesses you interact with through the platform.',
        },
      ],
    },
    {
      heading: '10. Content and Accuracy Disclaimers',
      content: [
        {
          type: 'p',
          text: 'Any scheduling summaries, wait-time approximations, text instructions, or descriptive content provided via our system are generated automatically or inputted directly by the business establishment. Sqip makes no warranties regarding the accuracy, and completeness.',
        },
      ],
    },
    {
      heading: '11. Governing Law',
      content: [
        {
          type: 'p',
          text: `These Terms are governed by the laws of ${COMPANY.jurisdiction}, and the applicable federal laws of Canada.`,
        },
      ],
    },
    {
      heading: '12. Changes to These Terms',
      content: [
        {
          type: 'p',
          text: 'We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the updated Terms.',
        },
        {
          type: 'p',
          text: 'We will update the "Last Updated" date at the top of this page when changes are made.',
        },
      ],
    },
    {
      heading: '13. Contact',
      content: [
        { type: 'p', text: `Questions about these Terms? Contact us at ${COMPANY.supportEmail}.` },
      ],
    },
  ],
};

// ── Business Terms of Service ─────────────────────────────────────────────────

const BUSINESS_TERMS = {
  slug: 'business-terms',
  title: 'Business Terms of Service',
  subtitle:
    'The terms that govern use of the sqip platform by businesses that manage queues and appointments for their customers and patients.',
  sections: [
    {
      heading: '1. The Service',
      content: [
        {
          type: 'p',
          text: `SQIP Technologies Inc. provides ${COMPANY.brand} (${COMPANY.website}), a software-as-a-service (SaaS) platform that enables businesses to manage virtual queues and appointments for their customers and patients. These Business Terms of Service ("Terms") govern your business's access to and use of the platform.`,
        },
      ],
    },
    {
      heading: '2. Eligibility and Account Setup',
      content: [
        {
          type: 'p',
          text: 'To use the platform as a business, you must register a business account and provide accurate account, contact, and billing information. You are responsible for the activity of all users you authorize under your account and for keeping account credentials secure.',
        },
      ],
    },
    {
      heading: '3. Your Compliance Responsibilities (PHIPA)',
      content: [
        {
          type: 'p',
          text: 'If your business is a health information custodian under Ontario\'s Personal Health Information Protection Act, 2004 (PHIPA), you remain the custodian of your patients\' personal health information. sqip acts solely as your agent and information processor — not the custodian. You are responsible for your own compliance with PHIPA and all other laws applicable to your collection and use of customer and patient information.',
        },
      ],
    },
    {
      heading: '4. Data Processing',
      content: [
        {
          type: 'p',
          text: 'sqip processes patient and customer data only to provide the queue and appointment service to your business, and only on your instructions. We do not use this data for our own purposes and we do not sell it.',
        },
      ],
    },
    {
      heading: '5. Consent from Your Customers',
      content: [
        {
          type: 'p',
          text: 'You are responsible for obtaining the necessary consent from your own patients and customers before adding them to a sqip queue or appointment, including consent to receive SMS notifications where applicable. You represent that you have obtained such consent for any individual whose information you enter into the platform.',
        },
      ],
    },
    {
      heading: '6. Automated Subscription Renewals',
      content: [
        {
          type: 'p',
          text: 'The platform is provided on a subscription basis. Fees are billed through Stripe according to the plan you select. Unless stated otherwise, subscriptions renew automatically for successive periods until cancelled. You are responsible for keeping valid payment details on file.',
        },
      ],
    },
    {
      heading: '7. Non-Refundability',
      content: [
        {
          type: 'p',
          text: 'Sqip retains absolute, unilateral discretion to modify subscription fees, billing structures, or pricing modules at any time. Clients on existing active subscription plans at the time of any price change are grandfathered in and will continue to be billed at their current rate for the duration of their subscription — no additional fees will apply to their plan. Price adjustments will apply only to new subscriptions initiated after the effective date of the change, or to existing subscriptions that are upgraded, downgraded, or renewed under a new plan. Exempt from these term is the fees for SMS and SMS notifications.',
        },
      ],
    },
    {
      heading: '8. Unilateral Price Adjustments',
      content: [
        {
          type: 'p',
          text: 'Sqip retains absolute, unilateral discretion to modify subscription fees, billing structures, or pricing modules at any time. Clients on existing active subscription plans at the time of any price change are grandfathered in and will continue to be billed at their current rate for the duration of their subscription — no additional fees will apply to their plan. Price adjustments will apply only to new subscriptions initiated after the effective date of the change, or to existing subscriptions that are upgraded, downgraded, or renewed under a new plan. Exempt from these term is the fees for SMS and SMS notifications.',
        },
      ],
    },
    {
      heading: '9. SMS Delivery Costs and Overage Charges',
      content: [
        {
          type: 'p',
          text: `SMS notifications sent to a business's patrons are delivered through a third-party messaging provider. The per-message cost of SMS delivery is currently USD ${COMPANY.smsPrice} but is subject to change at any time at the sole discretion of Sqip. As SMS notifications are core to the platform, no advance notice will be given when the per-message rate changes — the prevailing rate at the time of sending applies automatically. Each subscription plan includes an allocation of SMS notifications. If a registered business were to surpass their SMS allotment, the difference between the  SMS cost would be calculated and charged a separate overage fee. This overage will be collected either at the next subscription renewal date or upon cancellation of the service, whichever occurs first.`,
        },
      ],
    },
      {
  heading: '10. Email Delivery Costs and Overage Charges',
  content: [
      {
      type: 'p',
      text: `Email notifications sent to a business's patrons are delivered through a third-party email service provider. The per-message cost of email delivery is currently CAD $${COMPANY.emailPrice} but is subject to change at any time at the sole discretion of Sqip. As email notifications are core to the platform, no advance notice will be given when the per-message rate changes — the prevailing rate at the time of sending applies automatically. Each subscription plan includes an allocation of email notifications. If a registered business were to surpass their email allotment, the difference between the email cost would be calculated and charged as a separate overage fee. This overage will be collected either at the next subscription renewal date or upon cancellation of the service, whichever occurs first.`,
    },
    ],
    },
    {
  heading: '11. Service Guarantee',
  content: [
    {
      type: 'p',
      text: 'Sqip aims to make the platform available at least 98% of the time each calendar month measured as the percentage of each calendar month during which the platform is not experiencing a full outage. This Service Guarantee excludes downtime caused by scheduled maintenance, events outside our reasonable control (including force majeure events), and outages of third-party services we rely on.',
    },
    {
      type: 'p',
      text: 'This Service Guarantee is sqip\'s sole warranty regarding availability of the platform. If sqip fails to meet the Service Guarantee in a given month, sqip\'s sole obligation is to use commercially reasonable efforts to investigate and repair the affected part of the platform; the Service Guarantee does not entitle the Client to credits, refunds, or other compensation unless separately agreed in writing.',
    },
    {
      type: 'p',
      text: 'Except for the Service/Uptime Guarantee, the platform is provided on an "as is" and "as available" basis, without warranties of any kind, whether express or implied, including any warranty of satisfactory quality, merchantability, fitness for a particular purpose, or non-infringement.',
    },
  ],
},
    {
      heading: '12. Acceptable Use and Termination',
      content: [
        {
          type: 'p',
          text: 'You agree to use the platform lawfully and not to abuse, disrupt, or misuse it. sqip may suspend or terminate accounts that engage in abuse, fraud, illegal activity, or that materially breach these Terms.',
        },
      ],
      },
      {
      heading: '13. Late Enforcement and Suspension',
      content: [
        {
          type: 'p',
          text: 'In the event that a recurring transaction fails, a credit card is declined, or any invoice remains unpaid on its due date, Sqip grants itself the immediate right to execute late enforcement. The Client will be granted a strict 7-day grace period to update payment credentials and clear the balance. If the account remains delinquent on the 8th day following notice of payment failure, Sqip will completely suspend all virtual queue and appointment system access. This operational freeze will remain fully active, blocking both Client personnel and patrons from booking slots or entering queues, until all outstanding balances are cleared in full.',
        },
      ],
    },
    {
      heading: '14. Limitation of Liability',
      content: [
        {
          type: 'p',
          text: 'To the maximum extent permitted by law, sqip will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for lost profits, lost revenue, or business interruption, arising from or related to the platform. sqip\'s total aggregate liability for any claim arising out of or related to these Terms is limited to the total fees paid by your business to sqip in the three (3) months immediately preceding the event giving rise to the claim.',
        },
      ],
    },
    {
      heading: '15. Third-Party Liability Immunity',
      content: [
        {
          type: 'p',
            text: 'The operational workflows of these third-party providers exist outside the control of Sqip. The Client acknowledges and agrees that Sqip is not liable for any liabilities, data breaches, security incidents, processing errors, or technical glitches caused by third-party providers. Any errors, compromised credentials, or service failures occurring at the third-party provider level must be resolved exclusively between the Client and the relevant third-party provider.',
        },
      ],
    },
    {
      heading: '16. Governing Law',
      content: [
        {
          type: 'p',
          text: `These Terms are governed by the laws of ${COMPANY.jurisdiction}, and the applicable federal laws of Canada.`,
        },
      ],
    },
    {
      heading: '17. Changes to These Terms',
      content: [
        {
          type: 'p',
          text: 'We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after changes are posted constitutes acceptance of the updated Terms.',
        },
        {
          type: 'p',
          text: 'We will update the "Last Updated" date at the top of this page when changes are made.',
        },
      ],
    },
    {
      heading: '18. Contact',
      content: [
        { type: 'p', text: `Questions about these Terms? Contact us at ${COMPANY.supportEmail}.` },
      ],
    },
  ],
};

// ── Registry ──────────────────────────────────────────────────────────────────

export const LEGAL_DOCS = {
  privacy: PRIVACY,
  terms: USER_TERMS,
  'business-terms': BUSINESS_TERMS,
};

// Order shown on the /legal index page.
export const LEGAL_INDEX = [
  { slug: 'privacy', title: 'Privacy Policy', path: '/legal/privacy', description: 'How we collect, use, and protect your information (PIPEDA & PHIPA).' },
  { slug: 'terms', title: 'User Terms of Service', path: '/legal/terms', description: 'The terms for individuals joining queues and booking appointments.' },
  { slug: 'business-terms', title: 'Business Terms of Service', path: '/legal/business-terms', description: 'The terms for businesses using sqip to manage queues and appointments.' },
];
